import type { I18nNamespace } from './locales.js';

/**
 * Product registry. Each "product" is marketed as a standalone app but is just
 * a module of the same platform. The app shell renders navigation only for the
 * products a user has enabled; a user with exactly one product sees no switcher.
 */
export const PRODUCT_IDS = ['booking', 'agreements', 'invoicing', 'inquiry', 'clients'] as const;
export type ProductId = (typeof PRODUCT_IDS)[number];

export interface ProductDefinition {
  id: ProductId;
  /** i18n namespace holding the product's strings (name, nav labels, screens). */
  i18nNamespace: I18nNamespace;
  /** Base path of the product inside the authenticated app. */
  basePath: `/${string}`;
  /** Public marketing/landing route. */
  landingPath: `/${string}`;
  /** Lucide icon name used by the shell nav. */
  navIcon: string;
  /** Products not yet built are hidden from the "add product" screen. */
  status: 'available' | 'coming_soon';
}

export const PRODUCTS: Readonly<Record<ProductId, ProductDefinition>> = {
  booking: {
    id: 'booking',
    i18nNamespace: 'booking',
    basePath: '/app/booking',
    landingPath: '/booking',
    navIcon: 'calendar-check',
    status: 'available',
  },
  agreements: {
    id: 'agreements',
    i18nNamespace: 'common',
    basePath: '/app/agreements',
    landingPath: '/agreements',
    navIcon: 'file-signature',
    status: 'coming_soon',
  },
  invoicing: {
    id: 'invoicing',
    i18nNamespace: 'common',
    basePath: '/app/invoicing',
    landingPath: '/invoicing',
    navIcon: 'receipt',
    status: 'coming_soon',
  },
  inquiry: {
    id: 'inquiry',
    i18nNamespace: 'common',
    basePath: '/app/inquiry',
    landingPath: '/inquiry',
    navIcon: 'inbox',
    status: 'coming_soon',
  },
  clients: {
    id: 'clients',
    i18nNamespace: 'common',
    basePath: '/app/clients',
    landingPath: '/clients',
    navIcon: 'users',
    status: 'coming_soon',
  },
};

export function isProductId(value: unknown): value is ProductId {
  return typeof value === 'string' && (PRODUCT_IDS as readonly string[]).includes(value);
}

export const DEFAULT_SIGNUP_PRODUCT: ProductId = 'booking';
